import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
    FiHome, FiSettings, FiLogOut, FiMenu, FiX,
    FiChevronsLeft, FiChevronsRight,
    FiUsers, FiLink, FiBarChart2, FiMessageSquare,
    FiUserCheck, FiLayers, FiChevronDown,
    FiShoppingBag, FiEdit3, FiFileText, FiBell
} from "react-icons/fi";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../redux/store";
import { adminLogout } from "../redux/adminAuthSlice";
import LogoutConfirmModal from "../components/common/LogoutConfirmModal";
import SpotfixWidget from "../components/SpotfixWidget";

interface NavItem {
    name: string;
    path?: string;
    icon: React.ReactNode;
    badge?: string;
    children?: { name: string; path: string }[];
}

export default function AdminLayout() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const isFeedbackRoute = location.pathname.startsWith("/admin/feedbacks");
    const isAnalyticsRoute = location.pathname.startsWith("/admin/analytics");
    const [feedbackMenuOpen, setFeedbackMenuOpen] = useState(isFeedbackRoute);
    const [analyticsMenuOpen, setAnalyticsMenuOpen] = useState(isAnalyticsRoute);

    const { spotfixToken, user } = useSelector((state: RootState) => state.adminAuth);
    const activeSpotfixToken = spotfixToken || user?.spotfixToken || localStorage.getItem("admin_spotfix_token") || localStorage.getItem("spotfix_token");

    useEffect(() => {
        if (isFeedbackRoute) {
            setFeedbackMenuOpen(true);
        }
    }, [isFeedbackRoute]);

    useEffect(() => {
        if (isAnalyticsRoute) {
            setAnalyticsMenuOpen(true);
        }
    }, [isAnalyticsRoute]);

    const handleLogout = () => {
        dispatch(adminLogout());
        navigate("/admin/login");
    };

    const navItems: NavItem[] = [
        { name: "Dashboard", path: "/admin/dashboard", icon: <FiHome /> },
        { name: "Dealers", path: "/admin/dealers", icon: <FiUsers /> },
        { name: "Dealer Revenue", path: "/admin/dealers-by-tier", icon: <FiLayers /> },
        { name: "Account Migration", path: "/admin/onboarding", icon: <FiUserCheck /> },
        {
            name: "Analytics",
            path: "/admin/analytics",
            icon: <FiBarChart2 />,
            children: [
                { name: "Active Dealers", path: "/admin/analytics/active-dealers" },
                { name: "Top Locations", path: "/admin/analytics/locations" },
                { name: "Traffic Insights", path: "/admin/analytics/traffic" },
                { name: "Backlink Health", path: "/admin/analytics/backlinks" },
                { name: "Activity", path: "/admin/analytics/activity" },
            ]
        },
        {
            name: "Feedbacks",
            icon: <FiMessageSquare />,
            children: [
                { name: "Dealer Locator", path: "/admin/feedbacks/locator" },
                { name: "Dealer Portal", path: "/admin/feedbacks/portal" },
            ]
        },
        { name: "Page Management", path: "/admin/page-management", icon: <FiShoppingBag /> },
        // { name: "Leads", path: "/admin/leads", icon: <FiMail /> },
        // { name: "Tiers", path: "/admin/tiers", icon: <FiLayers /> },
        // { name: "Media", path: "/admin/media", icon: <FiImage /> },
        { name: "Profile Updates", path: "/admin/profile-update-requests", icon: <FiEdit3 /> },
        { name: "Content Requests", path: "/admin/content-requests", icon: <FiFileText /> },
        { name: "Verification", path: "/admin/backlinks", icon: <FiLink /> },
        { name: "Notifications", path: "/admin/notifications", icon: <FiBell />, badge: "Coming Soon" },
        { name: "Settings", path: "/admin/settings", icon: <FiSettings /> },
    ];

    const navSections: Record<number, string> = {
        0: "Overview",
        1: "Workspace",
        5: "Engagement",
        6: "Review queue",
        9: "Verification",
        10: "System",
    };

    return (
        <div className="flex h-screen bg-gray-50 font-poppins overflow-hidden">
            {/* MOBILE OVERLAY */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* SIDEBAR */}
            <aside
                className={`admin-sidebar-shell fixed inset-y-0 left-0 z-50 flex flex-col shadow-xl border-r transition-all duration-300 ease-in-out
                    md:relative md:translate-x-0 
                    ${isSidebarOpen ? "translate-x-0 w-72" : "-translate-x-full md:translate-x-0"}
                    ${isCollapsed ? "md:w-20" : "md:w-72"}
                `}
            >
                {/* Sidebar Header */}
                <div className={`admin-sidebar-header h-16 flex items-center ${isCollapsed ? "justify-center" : "justify-between px-5"} flex-shrink-0 transition-all duration-300`}>
                    {!isCollapsed && (
                        <div className="flex items-center gap-2">
                            <img
                                src="/logo.png"
                                alt="DECO TV Frames"
                                className="h-7 w-auto object-contain"
                            />
                            <div className="flex flex-col leading-tight">
                                <span className="text-sm font-bold text-[#382d23]">Admin Portal</span>
                                <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#8A6935]">Control center</span>
                            </div>
                        </div>
                    )}
                    {/* Desktop Toggle Button */}
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        aria-label={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                        aria-expanded={!isCollapsed}
                        className="hidden md:flex p-1.5 rounded-lg text-gray-400 hover:text-[#B08D55] hover:bg-[#B08D55]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#B08D55]/30"
                    >
                        {isCollapsed ? <FiChevronsRight size={20} /> : <FiChevronsLeft size={20} />}
                    </button>

                    {/* Mobile Close Button */}
                    <div className="md:hidden absolute top-4 right-4">
                        <button
                            onClick={() => setIsSidebarOpen(false)}
                            aria-label="Close Mobile Sidebar"
                            className="p-2 text-gray-400 hover:text-[#B08D55] transition-colors focus:outline-none focus:ring-2 focus:ring-[#B08D55]/30"
                        >
                            <FiX size={24} />
                        </button>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="admin-sidebar-nav flex-1 p-3 space-y-1 overflow-y-auto overflow-x-hidden" aria-label="Admin Main Navigation">
                    {navItems.map((item, index) => {
                        const sectionHeading = !isCollapsed && navSections[index] ? (
                            <div className="admin-nav-section" aria-hidden="true">
                                <span>{navSections[index]}</span>
                            </div>
                        ) : null;
                        if (item.children) {
                            const isAnalyticsMenu = item.name === "Analytics";
                            const isChildActive = isAnalyticsMenu ? isAnalyticsRoute : isFeedbackRoute;
                            const menuOpen = isAnalyticsMenu ? analyticsMenuOpen : feedbackMenuOpen;
                            const setMenuOpen = isAnalyticsMenu ? setAnalyticsMenuOpen : setFeedbackMenuOpen;
                            const firstChildPath = item.children[0]?.path || item.path;
                            return (
                                <div key={item.name} className="space-y-1">
                                    {sectionHeading}
                                    {isCollapsed ? (
                                        <button
                                            onClick={() => {
                                                setIsCollapsed(false);
                                                setMenuOpen(true);
                                                if (firstChildPath) navigate(firstChildPath);
                                            }}
                                            aria-label={item.name}
                                            data-active={isChildActive}
                                            className={`admin-nav-item flex items-center justify-center p-2 rounded-xl transition-all duration-200 w-full group font-medium ${
                                                isChildActive
                                                    ? "bg-[#B08D55]/10 text-[#B08D55]"
                                                    : "text-gray-500 hover:bg-gray-50 hover:text-[#B08D55]"
                                            }`}
                                            title={item.name}
                                        >
                                            <span className="admin-nav-icon text-lg flex-shrink-0">{item.icon}</span>
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => setMenuOpen(!menuOpen)}
                                                aria-expanded={menuOpen}
                                                data-active={isChildActive}
                                                className={`admin-nav-item flex items-center justify-between w-full px-3 py-2 rounded-xl transition-all duration-200 group font-medium ${
                                                    isChildActive
                                                        ? "bg-[#B08D55]/10 text-[#B08D55]"
                                                        : "text-gray-500 hover:bg-gray-50 hover:text-[#B08D55]"
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className="admin-nav-icon text-lg flex-shrink-0">{item.icon}</span>
                                                    <span className="whitespace-nowrap">{item.name}</span>
                                                </div>
                                                <FiChevronDown
                                                    size={16}
                                                    className={`transition-transform duration-200 ${
                                                        menuOpen ? "rotate-180 text-[#B08D55]" : "text-gray-400 group-hover:text-[#B08D55]"
                                                    }`}
                                                />
                                            </button>

                                            {menuOpen && (
                                                <div className="admin-nav-submenu ml-4 pl-3 pr-2 space-y-1 pt-1 pb-1">
                                                    {item.children.map((child) => (
                                                        <NavLink
                                                            key={child.path}
                                                            to={child.path}
                                                            onClick={() => setIsSidebarOpen(false)}
                                                            className={({ isActive }) =>
                                                                `admin-nav-subitem block px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                                                    isActive
                                                                        ? "bg-[#B08D55] text-white font-bold shadow-sm shadow-[#B08D55]/25"
                                                                        : "text-gray-500 hover:text-[#B08D55] hover:bg-gray-50"
                                                                }`
                                                            }
                                                        >
                                                            {child.name}
                                                        </NavLink>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        }

                        return (
                            <div key={item.path}>
                                {sectionHeading}
                                <NavLink
                                    to={item.path!}
                                    onClick={() => setIsSidebarOpen(false)}
                                    aria-label={item.name}
                                    className={({ isActive }) =>
                                        `admin-nav-item flex items-center gap-3 py-2 rounded-xl transition-all duration-200 group font-medium ${isActive
                                            ? "bg-[#B08D55]/10 text-[#B08D55]"
                                            : "text-gray-500 hover:bg-gray-50 hover:text-[#B08D55]"
                                        } ${isCollapsed ? "justify-center px-0" : "px-4"}`
                                    }
                                    title={isCollapsed ? (item.badge ? `${item.name} (${item.badge})` : item.name) : ""}
                                >
                                    <span className="admin-nav-icon text-lg flex-shrink-0">{item.icon}</span>
                                    {!isCollapsed && (
                                        <div className="flex items-center justify-between w-full min-w-0">
                                            <span className="whitespace-nowrap transition-opacity duration-300 opacity-100">{item.name}</span>
                                            {item.badge && (
                                                <span className="ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#B08D55]/15 text-[#8A6935] whitespace-nowrap">
                                                    {item.badge}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </NavLink>
                            </div>
                        );
                    })}
                </nav>

                {/* Footer */}
                <div className={`admin-sidebar-footer p-3 border-t ${isCollapsed ? "flex justify-center" : ""}`}>
                    <button
                        onClick={() => setIsLogoutModalOpen(true)}
                        aria-label="Logout of Admin Panel"
                        className={`flex items-center gap-3 px-4 py-2.5 text-[#675d53] hover:bg-rose-50 hover:text-rose-700 rounded-xl transition-colors font-medium w-full ${isCollapsed ? "justify-center px-0" : ""}`}
                        title={isCollapsed ? "Logout" : ""}
                    >
                        <FiLogOut size={20} />
                        {!isCollapsed && <span>Logout</span>}
                    </button>
                </div>
            </aside>

            {/* MAIN CONTENT */}
            <main className="flex-1 h-full overflow-y-auto relative w-full bg-gray-50">
                {/* Mobile Toggle Button */}
                <div className="md:hidden p-4 sticky top-0 z-30 bg-white/90 backdrop-blur-sm border-b border-gray-200">
                    <button
                        onClick={() => setIsSidebarOpen(true)}
                        aria-label="Open Mobile Sidebar"
                        aria-expanded={isSidebarOpen}
                        className="p-2 text-gray-600 hover:text-[#B08D55] rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#B08D55]/30"
                    >
                        <FiMenu size={24} />
                    </button>
                </div>

                <div className="p-4 md:p-6">
                    <Outlet />
                </div>
            </main>

            <LogoutConfirmModal
                isOpen={isLogoutModalOpen}
                onClose={() => setIsLogoutModalOpen(false)}
                onConfirm={handleLogout}
            />

            <SpotfixWidget userToken={activeSpotfixToken} />
        </div>
    );
}
